import * as React from 'react'
import { useCallback, useState } from 'react'

import {
  FutureDevelopment,
  LeadIcon,
  MoreButton,
  OpportunityIcon,
  ServiceIcon,
} from '@sales-center/components'
import { ILocation } from '@user/store/locations/typings'
import { LeadSummaryTable } from './LeadSummaryTable'
import { OppSummaryTable } from './OppSummaryTable'
import { SoldServicesTable } from './SoldServicesTable'
import { toast } from '@sales-center/components'
import { getStyles } from './useStyles'
import { useGetServiceLocationUrl } from '@user/store/authorization/hooks'

import { EntityCardBody } from '@user/components/EntityCards/Body'
import { EntityCardDrawer } from '@user/components/EntityCards/Drawer'
import { EntityCardFooter } from '@user/components/EntityCards/Footer'
import { EntityCardHeader } from '@user/components/EntityCards/Header'
import { EntityCardWrapper } from '@user/components/EntityCards/Wrapper'
import { EntityName } from '@user/components/EntityCards/atoms/EntityName'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { ENTITY_TYPES } from '@sales-center/common'
import { useTheme } from '@mui/material/styles'

interface Props {
  location: ILocation
}

export function LocationCard(props: Props) {
  const {
    id,
    firstName,
    lastName,
    externalLinkId,
    locationCode,
    street1,
    city,
    stateAbbreviation,
    postalCode,
    numLeads,
    numOpportunities,
    numOpenOpportunities,
    numSoldServices,
  } = props.location

  const [open, setOpen] = useState<boolean>(false)
  const handleFooterClick = useCallback(() => {
    setOpen(!open)
  }, [open])

  const serviceLocation = React.useMemo(
    () =>
      [street1, city, `${stateAbbreviation} ${postalCode}`]
        .filter((x) => x != '')
        .join(', '),
    [city, postalCode, stateAbbreviation, street1],
  )

  const locationUrl = useGetServiceLocationUrl(externalLinkId)
  const theme = useTheme()
  const styles = getStyles(theme)
  return (
    <EntityCardWrapper>
      <EntityCardHeader>
        <Box display="flex">
          <Box display="flex" flexGrow={1} alignItems="center">
            <Typography>Service Location {locationCode}</Typography>
          </Box>
          <Box>
            <Button
              sx={styles.viewButton}
              color="primary"
              onClick={() => window.open(locationUrl, '_blank')}
              variant="contained"
            >
              View Location in PestPac
            </Button>
            <MoreButton
              dataTestId={`locationCardMoreBtn-${id}`}
              options={[
                {
                  label: 'Edit',
                  onClick: () => toast.error('not implemented'),
                  dataTestId: 'locationCardEditOption',
                },
              ]}
            />
          </Box>
        </Box>
      </EntityCardHeader>
      <EntityCardBody>
        <Box>
          <EntityName name={`${firstName} ${lastName}`} entityId={id} />
        </Box>
        <Box mt={1}>
          <Typography>{serviceLocation}</Typography>
        </Box>
      </EntityCardBody>
      <EntityCardFooter
        schedulingVisible={false}
        entityType={ENTITY_TYPES.Lead}
        handleOpenDrawer={handleFooterClick}
        isOpen={open}
        entityId={id}
      >
        <Box display="inline-flex">
          <Box sx={styles.summaryItem}>
            <LeadIcon theme={theme} />
            <Typography sx={styles.summaryText}>
              {numLeads} {numLeads === 1 ? 'Lead' : 'Leads'}
            </Typography>
          </Box>
          <Box sx={styles.summaryItem}>
            <OpportunityIcon />
            <Typography sx={styles.summaryText}>
              {numOpportunities}{' '}
              {numOpportunities === 1 ? 'Opportunity' : 'Opportunities'}
              {numOpenOpportunities > 0 && ` (${numOpenOpportunities} Open)`}
            </Typography>
          </Box>
          <Box sx={styles.summaryItem}>
            <ServiceIcon />
            <Typography sx={styles.summaryText}>
              {numSoldServices} {numSoldServices === 1 ? 'Service' : 'Services'}
            </Typography>
          </Box>
        </Box>
      </EntityCardFooter>
      <EntityCardDrawer isOpen={open}>
        <Box sx={(styles.detailSection, styles.history)}>
          History and Details
        </Box>

        {numLeads > 0 && (
          <>
            <Box sx={styles.detailSection}>
              <Box sx={styles.sectionHeader}>Leads</Box>
              <LeadSummaryTable externalLinkId={externalLinkId} />
            </Box>
          </>
        )}

        {numOpportunities > 0 && (
          <>
            <Box sx={styles.detailSection}>
              <Box sx={styles.sectionHeader}>Opportunities</Box>
              <OppSummaryTable externalLinkeId={externalLinkId} />
            </Box>
          </>
        )}

        <FutureDevelopment>
          <Box sx={styles.detailSection} style={{ height: '250px' }}>
            <Box sx={styles.sectionHeader}>Sold Services</Box>
            <SoldServicesTable externalLinkId={externalLinkId} />
          </Box>
        </FutureDevelopment>
      </EntityCardDrawer>
    </EntityCardWrapper>
  )
}
